import { useState } from "react";
import { toast } from "sonner";
import { Trash2, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

/** Admin-only destructive action: wipe every issue (with comments + attachments). */
export function PurgeIssuesDialog({
  projectId,
  projectCode,
  total,
  mode = "issues",
  open,
  onOpenChange,
  onDone,
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const commentsOnly = mode === "comments";
  const statusesMode = mode === "statuses";
  const logsMode = mode === "logs";

  let heading = `Delete all issues in ${projectCode}?`;
  if (statusesMode) heading = `Reset all statuses in ${projectCode}?`;
  else if (logsMode) heading = `Reset all logs in ${projectCode}?`;
  else if (commentsOnly) heading = `Delete all comments in ${projectCode}?`;

  const purge = async () => {
    setBusy(true);
    try {
      if (logsMode) {
        const { data } = await api.post(`/projects/${projectId}/panels/history/reset`, {
          confirm_code: code,
        });
        toast.success(`${data.logs_deleted} log records deleted`);
        onOpenChange(false);
        setCode("");
        onDone?.();
        return;
      }
      if (statusesMode) {
        const { data } = await api.post(`/projects/${projectId}/panels/statuses/reset`, {
          confirm_code: code,
        });
        toast.success(`Statuses reset on ${data.panels_reset} panels`);
        onOpenChange(false);
        setCode("");
        onDone?.();
        return;
      }
      const url = commentsOnly
        ? `/projects/${projectId}/issues/comments`
        : `/projects/${projectId}/issues`;
      const { data } = await api.delete(url, { data: { confirm_code: code } });
      toast.success(
        commentsOnly
          ? `Comments cleared: ${data.issues_cleared} issues, ${data.panel_notes_deleted} panel notes`
          : `${data.deleted} issues deleted`
      );
      onOpenChange(false);
      setCode("");
      onDone?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="purge-issues-dialog"
      className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4"
    >
      <div className="w-full max-w-md rounded-2xl border border-red-500/30 bg-white p-6 okn-card-shadow">
        <p className="font-display text-lg font-semibold text-slate-900">{heading}</p>
        <p className="mt-2 text-sm text-slate-600">
          {logsMode ? (
            <>
              This permanently deletes the whole activity log of this project — every status change,
              shipping, delivery and admin record shown in panel history. Panels, statuses, issues
              and notes stay in place. This cannot be undone.
            </>
          ) : statusesMode ? (
            <>
              Every panel in this project goes back to its initial state: department statuses reset
              to their defaults (Not Started / Not Inspected / Not Installed), blocked flags removed
              and shipping method / shipment code / ETA wiped. Panels, issues, notes and the history
              log stay in place. This cannot be undone.
            </>
          ) : commentsOnly ? (
            <>
              This permanently removes every issue comment <b>and every panel note</b> in this
              project. The issues, their status and attachments stay in place. This cannot be
              undone.
            </>
          ) : (
            <>
              This permanently removes <b>{total}</b> issues together with all their comments and
              attachments. Panels, statuses and history are not affected. This cannot be undone.
            </>
          )}
        </p>
        <p className="mono mt-4 text-[10px] uppercase tracking-[0.18em] text-slate-500">
          Type {projectCode} to confirm
        </p>
        <input
          data-testid="purge-confirm-input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={projectCode}
          className="mono mt-1.5 h-9 w-full rounded-md border border-slate-200 bg-[#EEF2F8] px-2 text-xs text-slate-800"
        />
        <div className="mt-5 flex gap-2">
          <Button
            data-testid="purge-cancel-button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="h-9 flex-1 rounded-full text-xs text-slate-600"
          >
            Cancel
          </Button>
          <Button
            data-testid="purge-confirm-button"
            onClick={purge}
            disabled={busy || code.trim().toUpperCase() !== (projectCode || "").toUpperCase()}
            className="h-9 flex-1 rounded-full bg-red-600 text-xs text-white hover:bg-red-700 disabled:opacity-50"
          >
            {busy ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <>
                <Trash2 size={13} className="mr-1.5" />{" "}
                {statusesMode ? "Reset statuses" : logsMode ? "Reset logs" : "Delete permanently"}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
